import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement
);

const PerformanceCharts = ({ goalAnalytics, okrAnalytics, performanceAnalytics, userRole }) => {
  
  // Goal Progress Trend Chart
  const goalTrendData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Goal Completion %',
        data: [45, 52, 58, 65, 72, goalAnalytics?.completion_rate || 75],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
      },
      {
        label: 'Average Progress %',
        data: [40, 48, 55, 62, 68, goalAnalytics?.avg_progress || 70],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
      }
    ],
  };

  const goalTrendOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Goal Progress Trends (6 Months)',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
      },
    },
  };

  // OKR Score Distribution
  const okrScoreData = {
    labels: ['0-20%', '21-40%', '41-60%', '61-80%', '81-100%'],
    datasets: [
      {
        label: 'OKR Score Distribution',
        data: [5, 12, 25, 35, 23], // Simulated data
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',   // Red
          'rgba(245, 158, 11, 0.8)',  // Orange  
          'rgba(251, 191, 36, 0.8)',  // Yellow
          'rgba(34, 197, 94, 0.8)',   // Green
          'rgba(16, 185, 129, 0.8)',  // Emerald
        ],
        borderColor: [
          'rgba(239, 68, 68, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(251, 191, 36, 1)',
          'rgba(34, 197, 94, 1)',
          'rgba(16, 185, 129, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const okrScoreOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'OKR Score Distribution',
      },
    },
  };

  // Performance by Category
  const categoryData = {
    labels: ['Communication', 'Leadership', 'Technical', 'Innovation', 'Teamwork', 'Results'],
    datasets: [
      {
        label: 'Performance Score',
        data: [85, 78, 92, 88, 90, 86],
        backgroundColor: 'rgba(139, 92, 246, 0.8)',
        borderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 1,
      },
    ],
  };

  const categoryOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Performance by Category',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
      },
    },
  };

  // Team Comparison (for managers and above)
  const teamComparisonData = {
    labels: ['Team A', 'Team B', 'Team C', 'Team D', 'Your Team'],
    datasets: [
      {
        label: 'Goal Completion %',
        data: [72, 85, 68, 91, 78],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
      },
      {
        label: 'OKR Score %',
        data: [68, 82, 65, 88, 75],
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
      },
    ],
  };

  const teamComparisonOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Team Performance Comparison',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
      },
    },
  };

  // Quarterly Performance Overview
  const quarterlyData = {
    labels: ['Q1 2024', 'Q2 2024', 'Q3 2024', 'Q4 2024'],
    datasets: [
      {
        label: 'Goals Completed',
        data: [12, 15, 18, goalAnalytics?.completed_goals || 14],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
      },
      {
        label: 'OKRs Completed', 
        data: [3, 4, 5, 4],
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
      },
      {
        label: 'Reviews Completed',
        data: [1, 0, 1, performanceAnalytics?.completed_reviews || 0],
        backgroundColor: 'rgba(139, 92, 246, 0.8)',
      },
    ],
  };

  const quarterlyOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Quarterly Performance Overview',
      },
    },
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Goal Progress Trends */}
      <div className="bg-white p-6 rounded-lg shadow">
        <Line data={goalTrendData} options={goalTrendOptions} />
      </div>

      {/* OKR Score Distribution */}
      <div className="bg-white p-6 rounded-lg shadow">
        <Doughnut data={okrScoreData} options={okrScoreOptions} />
      </div>

      {/* Performance by Category */}
      <div className="bg-white p-6 rounded-lg shadow">
        <Bar data={categoryData} options={categoryOptions} />
      </div>

      {/* Team Comparison (for managers+) */}
      {['manager', 'hr', 'executive', 'admin'].includes(userRole) && (
        <div className="bg-white p-6 rounded-lg shadow">
          <Bar data={teamComparisonData} options={teamComparisonOptions} />
        </div>
      )}

      {/* Quarterly Overview */}
      <div className="bg-white p-6 rounded-lg shadow lg:col-span-2">
        <Bar data={quarterlyData} options={quarterlyOptions} />
      </div>
    </div>
  );
};

export default PerformanceCharts;